import './styles/styles.css'

import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react'
import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EditorHeaderSlot } from '@editor/EditorProvider'
import LoadingPage from '@editor/components/LoadingPage'
import PageErrorHandler from '@editor/components/PageErrorHandler'
import { BLUEPRINT_PROJECTION_LIMIT_POINTS_MILLION, USER_TYPES } from '@editor/config/constants'
import { UserContext } from '@editor/contexts/Users'
import { useDocumentTitle } from '@editor/hooks/useDocumentTitle'
import { useHashUpdater } from '@editor/hooks/useHashUpdater'
import useInspectionGroup from '@editor/hooks/useInspectionGroup'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { decideActionPermission } from '@editor/services/Validation'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setConnectedAreas } from '@editor/store/page'
import mixpanel from 'mixpanel-browser'
import { IoWarning } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Matrix4, OrthographicCamera, PerspectiveCamera, Points, Scene, Vector3 } from 'three'

import AttentionText from '../common/AttentionText/AttentionText'
import MissingPCDFIle from '../common/MissingPCDFIle'
import { useInspectionItems } from '../hooks/useInspectionItems'
import { useShapes } from '../hooks/useShapes'
import { collectConnectedAreas, connectedAreasToSerializable } from '../pcd-alignment/utils'
import MainCanvas from './MainCanvas'
import Canvas2D from './components/Canvas2D'
import InfoPanels from './components/InfoPanels'
import NoBlueprints from './components/NoBlueprints'
import ToolComponents from './components/ToolComponents'
import Toolbar from './components/Toolbar'
import { BlueprintEditorContext, BlueprintEditorContextState } from './contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from './definitions'
import useConfigureCanvas from './hooks/useConfigureCanvas'
import useDrawings from './hooks/useDrawings'
import useExitModePrevention from './hooks/useExitModePrevention'
import {
  fetchBlueprints,
  reset,
  resetWorkingState,
  setEditorLoadStartTime,
  setIsAllowedModify,
  setPermissionSet,
  setSelectedTool,
} from './store/blueprintEditor'
import { resetDrawings } from './store/drawings'
import { Tools } from './tools/setup'
import { isProjectionTooManyPointsError } from './utils'

const BlueprintEditor = () => {
  // URL Params
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Contexts
  const { getAccessToken } = useContext(UserContext)

  // Hooks
  const { fetchAll } = useInspectionItems(true)
  const { fetchShapes } = useShapes(true)
  const { fetchInspectionGroups } = useInspectionGroup(true)
  const {
    shouldPreventExitEditingMode,
    shouldTogglePreventExitEditingMode,
    checkPreventExitEditingMode,
    resetPreventExitEditingMode,
  } = useExitModePrevention()
  const { resetBlueprintDrawings } = useDrawings()
  const { updateHash } = useHashUpdater('bp')

  // Refs
  const sceneRef = useRef<Scene>(null)
  const perspectiveCamera = useRef<PerspectiveCamera>(new PerspectiveCamera(90, 1, 0.1, 2000))
  const orthographicCamera = useRef<OrthographicCamera>(new OrthographicCamera(-1, 1, 1, -1, 0, 1))
  const activeCamera = useRef<PerspectiveCamera | OrthographicCamera>(perspectiveCamera.current)
  const arcballControls = useRef<ArcballControls | null>(null)
  const isAdjustingRef = useRef(false)

  // State
  const [pointClouds, setPointClouds] = useState<PointCloudOctree[]>([])
  const [pointCloud2ds, setPointCloud2ds] = useState<(Points | PointCloudOctree)[]>([])

  // Store
  const dispatch = useAppDispatch()
  const userType = useSelector((state: RootState) => state.user.userType) as keyof typeof USER_TYPES
  const userProfile = useSelector((state: RootState) => state.user.userProfile)

  const isLoading = useSelector((state: RootState) => state.page.isLoading)
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const isInvited = useSelector((state: RootState) => state.page.isInvited)
  const isOwner = useSelector((state: RootState) => state.page.isOwner)
  const isPageLoading = useSelector((state: RootState) => state.page.isLoading)

  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)
  const isPointCloud2dLoading = useSelector((state: RootState) => state.blueprint.editor.isPointCloud2dLoading)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const blueprints = useSelector((state: RootState) => state.blueprint.editor.blueprints)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)
  const isBlueprintsLoading = useSelector((state: RootState) => state.blueprint.editor.isLoading)

  // Show the 注目領域 recovery guidance only when the projection failed for too many points
  // AND the user can actually set a mask; viewers fall back to the generic failure message.
  const showMaskGuidance = isProjectionTooManyPointsError(selectedBlueprint) && isAllowedToModify
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)

  // page title
  useDocumentTitle(
    [
      !inspectionArea && !project ? '3D InfraLoop' : null,
      inspectionArea ? inspectionArea.inspection_area_name : null,
      project ? project.project_name : null,
    ]
      .filter(Boolean)
      .join(' · '),
  )

  // Vars
  const isHardLoading = isPageLoading // don't render anything until this finishes

  /**
   * Mixpanel super properties - stores currently registered properties for cleanup
   */
  const mixpanelSuperPropsRef = useRef<Record<string, string | number | undefined>>({})

  // Cleanup super properties on unmount
  useEffect(() => () => Object.keys(mixpanelSuperPropsRef.current).forEach((prop) => mixpanel.unregister(prop)), [])

  // Update mixpanelSuperPropsRef and register properties whenever dependencies change
  useEffect(() => {
    mixpanelSuperPropsRef.current = {
      Page: 'Blueprint Editor',
      'Project ID': project?.project_id ?? undefined,
      'Inspection Area ID': inspection_area_id ?? undefined,
      'Original File Name': inspectionArea?.origin_file?.name ?? undefined,
      'Blueprint ID': !isCreatingBlueprint ? (selectedBlueprint?.blueprint_id ?? undefined) : undefined,
      'Blueprint Name': selectedBlueprint?.name ?? undefined,
    }

    // Register the updated properties with Mixpanel
    mixpanel.register(mixpanelSuperPropsRef.current)
  }, [project, inspection_area_id, inspectionArea?.origin_file?.name, selectedBlueprint, isCreatingBlueprint])

  useEffect(() => {
    void Promise.all([fetchAll(), fetchShapes(), fetchInspectionGroups()])

    // Set editor load start time for performance tracking and session tracking
    const startTime = Date.now()
    dispatch(setEditorLoadStartTime(startTime))

    // Track Blueprint Editor Session start
    mixpanel.track('Blueprint Editor Session', {
      Action: 'start',
      'Area ID': inspection_area_id,
    })

    // Track session end on unmount
    return () => {
      const sessionDuration = Math.round((Date.now() - startTime) / 1000) // in seconds
      mixpanel.track('Blueprint Editor Session', {
        Action: 'end',
        Duration: sessionDuration,
        'Area ID': inspection_area_id,
      })
    }
  }, [fetchAll, fetchShapes, fetchInspectionGroups, dispatch, inspection_area_id])

  /**
   * Get permission set for the Editor
   */
  useEffect(() => {
    if (isLoading || !userProfile) return

    const permissionSet = decideActionPermission(isOwner, isInvited, userProfile.owned_products).BLUEPRINT
    dispatch(setPermissionSet(permissionSet))
    dispatch(setIsAllowedModify(permissionSet.MODIFY.includes(userType)))
  }, [isLoading, isInvited, isOwner, userProfile, userType, dispatch])

  /**
   * Load blueprints
   */
  useEffect(() => {
    void (async () => {
      if (!project || !inspectionArea?.inspection_area_id) return

      resetBlueprintDrawings()

      const token = await getAccessToken()
      if (!token) return

      await dispatch(
        fetchBlueprints({
          accessToken: token,
          projectId: project.project_id,
          areaId: inspectionArea.inspection_area_id,
        }),
      )
    })()
  }, [project, inspectionArea?.inspection_area_id, getAccessToken, dispatch, resetBlueprintDrawings])

  /**
   * Generate connected areas for loading all aligned PCDs.
   */
  useEffect(() => {
    if (!inspectionArea) return

    dispatch(
      setConnectedAreas(
        connectedAreasToSerializable(
          collectConnectedAreas(
            {
              current: {
                area: inspectionArea,
                blueprint: selectedBlueprint?.sort_key ? selectedBlueprint : undefined, // blueprint during creation has no sort_key
                anchors: [],
                center: new Vector3(),
              },
              matrix: new Matrix4().identity(),
              inverted: false,
            },
            inspectionArea,
            inspectionAreas,
            selectedBlueprint?.sort_key ? selectedBlueprint : undefined, // blueprint during creation has no sort_key
          ),
        ),
      ),
    )
  }, [inspectionArea, inspectionAreas, selectedBlueprint, dispatch])

  /**
   * Context value for BlueprintEditor
   */
  const contextValue = useMemo(
    (): BlueprintEditorContextState => ({
      perspectiveCamera,
      orthographicCamera,
      activeCamera,
      arcballControls,

      sceneRef,

      pointClouds,
      setPointClouds,
      pointCloud2ds,
      setPointCloud2ds,

      isAdjustingRef,

      shouldPreventExitEditingMode,
      shouldTogglePreventExitEditingMode,
      checkPreventExitEditingMode,
      resetPreventExitEditingMode,
    }),
    [
      perspectiveCamera,
      orthographicCamera,
      activeCamera,
      arcballControls,

      sceneRef,

      pointClouds,
      setPointClouds,
      pointCloud2ds,
      setPointCloud2ds,

      shouldPreventExitEditingMode,
      shouldTogglePreventExitEditingMode,
      checkPreventExitEditingMode,
      resetPreventExitEditingMode,
    ],
  )

  // Setup controls for the canvas
  const { setupControls } = useConfigureCanvas(contextValue)

  // Tools activation by running hook
  // Regardless if user can't use it, the hook must run.
  const toolsConfig = Tools.map((tool) => {
    const tools: (
      | {
          key: string
          auth: boolean
          hook: EditorConfig
        }
      | undefined
    )[] = []
    if (tool.hooks?.useEditor) {
      tools.push({
        key: tool.key,
        auth: tool.authCheck(userProfile, isAllowedToModify),
        hook: tool.hooks?.useEditor(contextValue),
      })
    }

    if (tool.toolbar?.variants) {
      tools.push(
        ...tool.toolbar.variants.map(
          (variant) =>
            variant.hooks?.useEditor && {
              key: variant.key,
              auth: variant.authCheck(userProfile, isAllowedToModify),
              hook: variant.hooks?.useEditor(contextValue),
            },
        ),
      )
    }

    return tools
  })
    .filter(Boolean)
    .flat()

  /**
   * If area has already been set and inspection_area_id is different, reset store/states
   */
  useEffect(() => {
    if (initCompleted && inspection_area_id && inspectionArea?.inspection_area_id !== inspection_area_id) {
      dispatch(resetWorkingState())
      dispatch(resetDrawings())
      void setupControls(Mode.Editor2D)
    }
  }, [initCompleted, inspection_area_id, inspectionArea?.inspection_area_id, setupControls, dispatch])

  /**
   * Set blueprint hash in URL when selectedBlueprint changes
   * This is to allow sharing of specific blueprint via URL
   */
  useEffect(() => {
    if (!selectedBlueprint?.sort_key) return
    updateHash(selectedBlueprint.blueprint_id)
  }, [selectedBlueprint, updateHash])

  /**
   * Cleanup on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  // PCD file is not uploaded yet
  if (
    inspectionArea &&
    (!inspectionArea.down_sampled_file?.name ||
      inspectionArea.downsample_status?.status !== FileProcessingStatus.SUCCEEDED)
  ) {
    return <MissingPCDFIle pageName="設計図面" />
  }

  return (
    <PageErrorHandler>
      <VStack
        m={0}
        w="100svw"
        h="100svh"
        backgroundColor="black"
        className="page blueprint-editor"
        userSelect="none"
        gap={0}
        overflow="hidden"
      >
        <EditorHeaderSlot />

        <BlueprintEditorContext.Provider value={contextValue}>
          {isHardLoading ? (
            <Box position="relative" overflow="hidden" flex={1} w="100%">
              <LoadingPage text="読み込み中…" />
            </Box>
          ) : (
            <Box className="blueprint-canvas" h="100svh" w="100svw" position="relative">
              {(!initCompleted || isBlueprintsLoading) && <LoadingPage zIndex={200} />}

              {!isBlueprintsLoading && !blueprints.length && !selectedBlueprint ? <NoBlueprints /> : null}

              {isPointCloud2dLoading && blueprints.length && selectedBlueprint && <LoadingPage zIndex={15} />}

              {mode === Mode.Editor2D &&
                selectedBlueprint?.project_3d_pcd_to_2d_pcd_status?.status === FileProcessingStatus.RUNNING && (
                  <LoadingPage text="ファイルを変換中..." zIndex={15} />
                )}

              {mode === Mode.Editor2D &&
              selectedBlueprint?.project_3d_pcd_to_2d_pcd_status?.status === FileProcessingStatus.FAILED ? (
                <VStack p={0} m={0} w="100%" h="100svh" gap={0} position="absolute" top={0} left={0} zIndex={15}>
                  <Flex
                    bg="black"
                    flex={1}
                    w="100%"
                    color="white"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    gap={3}
                  >
                    <IoWarning size="7rem" color="yellow" />
                    <Text fontSize="lg" color="gray2.300" whiteSpace="pre-line" textAlign="center">
                      {showMaskGuidance
                        ? `設計図面の対象範囲が${BLUEPRINT_PROJECTION_LIMIT_POINTS_MILLION}M点を超えています。\n${BLUEPRINT_PROJECTION_LIMIT_POINTS_MILLION}M点未満になるよう注目領域を設定してください。`
                        : 'ファイル変換に失敗しました。新しいブループリントを作成するか、サポートに連絡してください。'}
                    </Text>
                    {showMaskGuidance ? (
                      <Button
                        variant="panel-dark-outline"
                        size="sm"
                        fontWeight="normal"
                        onClick={() => dispatch(setSelectedTool(BlueprintEditorTools.MaskPCD))}
                      >
                        注目領域を設定
                      </Button>
                    ) : null}
                  </Flex>
                </VStack>
              ) : null}

              <Canvas2D />

              <MainCanvas />

              <Toolbar />

              <ToolComponents />

              <InfoPanels toolsConfig={toolsConfig} />
            </Box>
          )}
        </BlueprintEditorContext.Provider>

        <AttentionText />
      </VStack>
    </PageErrorHandler>
  )
}

export default BlueprintEditor
